/**
 * StakeholderAutocomplete Component
 * Reusable autocomplete with search filtering and inline "Create New" option
 * for Investor, Broker, and Collateral Provider fields.
 */

import React, { useState } from 'react';
import { Autocomplete, TextField, Box, Typography, Chip, createFilterOptions } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTenant } from '../../contexts/TenantContext';
import { MODULES, PERMISSION_LEVELS } from '../../contexts/PermissionConstants';
import StakeholderFormDialog from './StakeholderFormDialog';

const filter = createFilterOptions();
// Issuer / Investor Group autocomplete also searches by the 5-char site code.
const issuerFilter = createFilterOptions({
  stringify: (o) => `${o.name || ''} ${o.site || ''}`,
});

const TYPE_LABELS = {
  investor: 'Investor',
  broker: 'Broker',
  'collateral-provider': 'Collateral Provider',
  issuer: 'Issuer',
};

function StakeholderAutocomplete({
  stakeholderType,
  options = [],
  value,
  onChange,
  onStakeholderCreated,
  // T3-131: fired when an inline-created investor also created a linked broker record, so
  // the caller can refresh its broker list. Optional — pickers with no broker list ignore it.
  onLinkedBrokerCreated,
  label,
  error,
  helperText,
  disabled = false,
  countries = [],
}) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const { hasPermission } = usePermissions();
  const { terminology } = useTenant();

  const canCreate = hasPermission(MODULES.STAKEHOLDER_MANAGEMENT, PERMISSION_LEVELS.WRITE);
  const typeLabel = stakeholderType === 'issuer'
    ? (terminology?.['stakeholder.issuer.label'] || 'Issuer')
    : (TYPE_LABELS[stakeholderType] || 'Stakeholder');

  const selectedOption = options.find((opt) => opt.id === value) || null;

  const handleChange = (_, newValue) => {
    if (newValue && newValue._isCreateNew) {
      setCreateDialogOpen(true);
      return;
    }
    onChange(newValue?.id || '');
  };

  // Map 'collateral-provider' to 'provider' for the dialog component
  const dialogType = stakeholderType === 'collateral-provider' ? 'provider' : stakeholderType;

  const handleSaved = (savedData) => {
    // Build a select-list-shaped object matching what the /select endpoint returns
    const selectItem = { id: savedData.id, name: savedData.name, type: savedData.type };

    if (stakeholderType === 'investor') {
      // Find currency code from countries
      const currencyCountry = countries.find(c => c.id === savedData.currency_id);
      selectItem.currency_code = currencyCountry?.currency_code || '';
      selectItem.wht_rate = savedData.wht_rate || 0;
      selectItem.wht_gross_up = savedData.wht_gross_up || false;
      selectItem.tax_classification = savedData.tax_classification || null;
      selectItem.is_staff = savedData.is_staff || false;

      // T3-131: creating an investor with "also acts as a broker" ALSO creates a broker
      // record server-side. The broker dropdown is loaded once when the wizard mounts, so
      // without this it would not appear until a reload — the user would tick the box and
      // then be unable to find the broker they just made. We do not know the new broker's
      // id here (the create response returns the investor's), so the caller re-fetches.
      if (savedData.acts_as_broker && onLinkedBrokerCreated) {
        onLinkedBrokerCreated();
      }
    } else if (stakeholderType === 'broker') {
      selectItem.is_preferred = savedData.is_preferred || false;
      // T3-131: a broker created directly here is standalone, never investor-backed — the
      // link is only ever made from the investor side. Set explicitly so the option shape
      // matches the /select endpoint and the chip does not render off an undefined field.
      selectItem.linked_investor_id = null;
    } else if (stakeholderType === 'collateral-provider') {
      selectItem.credit_rating = savedData.credit_rating || null;
    } else if (stakeholderType === 'issuer') {
      selectItem.site = savedData.site || '';
    }

    if (onStakeholderCreated) {
      onStakeholderCreated(selectItem);
    }
    setCreateDialogOpen(false);
  };

  return (
    <>
      <Autocomplete
        options={options}
        value={selectedOption}
        onChange={handleChange}
        disabled={disabled}
        getOptionLabel={(option) => {
          if (option._isCreateNew) return '';
          return option.name || '';
        }}
        isOptionEqualToValue={(option, val) => option.id === val?.id}
        filterOptions={(opts, params) => {
          // Only show results when the user has typed something
          if (!params.inputValue) {
            return canCreate
              ? [{ _isCreateNew: true, name: `+ Create New ${typeLabel}` }]
              : [];
          }
          const activeFilter = stakeholderType === 'issuer' ? issuerFilter : filter;
          const filtered = activeFilter(opts, params);
          if (canCreate) {
            filtered.push({
              _isCreateNew: true,
              name: `+ Create New ${typeLabel}`,
            });
          }
          return filtered;
        }}
        renderOption={(props, option) => {
          if (option._isCreateNew) {
            const { key, ...restProps } = props;
            return (
              <Box
                component="li"
                key="create-new"
                {...restProps}
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  color: 'primary.main',
                  fontWeight: 600,
                }}
              >
                <AddIcon sx={{ mr: 1, fontSize: 20 }} />
                <Typography variant="body2" color="primary" fontWeight={600}>
                  Create New {typeLabel}
                </Typography>
              </Box>
            );
          }
          const { key, ...restProps } = props;
          return (
            <li key={option.id} {...restProps}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  {option.type && (
                    <Typography variant="caption" color="text.secondary">
                      {option.type}
                    </Typography>
                  )}
                </Box>
                {stakeholderType === 'issuer' && option.site && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace', fontWeight: 600, ml: 2 }}
                  >
                    {option.site}
                  </Typography>
                )}
                {/* T3-131: a broker backed by an investor record. Tagging it stops the entry
                    reading as an accidental duplicate of the investor of the same name. */}
                {stakeholderType === 'broker' && option.linked_investor_id && (
                  <Chip
                    label="Investor"
                    size="small"
                    variant="outlined"
                    color="info"
                    sx={{ ml: 2, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                  />
                )}
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={error}
            helperText={helperText}
            placeholder={
              stakeholderType === 'issuer'
                ? `Search ${typeLabel.toLowerCase()}s by name or site...`
                : `Search ${typeLabel.toLowerCase()}s...`
            }
          />
        )}
        noOptionsText={
          canCreate
            ? `No ${typeLabel.toLowerCase()}s found`
            : `No ${typeLabel.toLowerCase()}s found`
        }
      />

      <StakeholderFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSaved={handleSaved}
        stakeholderType={dialogType}
        editingItem={null}
        countries={countries}
      />
    </>
  );
}

export default StakeholderAutocomplete;
